import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { getDocumentIntelligenceSummary } from '../utils/documentIntelligence';
import { detectUploadField } from '../utils/uploadFieldMapping';
import { extractTabularData } from '../utils/tabularUpload';

const FIELD_LABELS = {
  unit: 'Unit Number',
  name: 'Tenant Name',
  monthlyRent: 'Monthly Rent',
  currentBalance: 'Current Balance',
  phone: 'Phone',
  email: 'Email',
  preferredLanguage: 'Preferred Language',
  skip: 'Skip / Ignore',
};

function cleanValue(value, field) {
  if (value === null || value === undefined) {
    return '';
  }

  let normalized = String(value).trim();
  if (normalized.toLowerCase() === 'n/a' || normalized.toLowerCase() === 'none' || normalized === '-') {
    return '';
  }

  if (field === 'monthlyRent' || field === 'currentBalance') {
    normalized = normalized.replace(/[$,\s]/g, '');
    const numericValue = parseFloat(normalized);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }

  if (field === 'phone') {
    return normalized.replace(/[^\d\s()+-]/g, '').trim();
  }

  if (field === 'preferredLanguage') {
    const lowerValue = normalized.toLowerCase();
    if (lowerValue.includes('span') || lowerValue === 'es') {
      return 'es';
    }
    return 'en';
  }

  return normalized;
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const extension = file.name.split('.').pop().toLowerCase();

    if (extension === 'csv') {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: false,
        skipBOM: true,
        complete: (results) => {
          const parsedTable = extractTabularData(results.data || []);
          resolve({
            fileName: file.name,
            headers: parsedTable.headers,
            rows: parsedTable.rows,
            sheets: null,
            pdfOnly: false,
          });
        },
        error: reject,
      });
      return;
    }

    if (extension === 'xlsx' || extension === 'xls') {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          const sheetNames = workbook.SheetNames;
          const sheets = {};

          for (const sheetName of sheetNames) {
            const worksheet = workbook.Sheets[sheetName];
            const matrixRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            const parsedTable = extractTabularData(matrixRows);
            sheets[sheetName] = { headers: parsedTable.headers, rows: parsedTable.rows };
          }

          resolve({
            fileName: file.name,
            sheets,
            selectedSheet: sheetNames[0],
            pdfOnly: false,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    if (extension === 'pdf') {
      resolve({
        fileName: file.name,
        headers: [],
        rows: [],
        sheets: null,
        pdfOnly: true,
      });
      return;
    }

    reject(new Error('Unsupported file type. Please upload .csv, .xlsx, .xls, or export PDFs to spreadsheet format first.'));
  });
}

function formatCellValue(value) {
  if (value === '' || value === null || value === undefined) {
    return '-';
  }
  return String(value);
}

export default function SmartUploader({ onDataParsed, isLoading }) {
  const [step, setStep] = useState('upload');
  const [dragOver, setDragOver] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mappings, setMappings] = useState({});
  const [isPdfOnly, setIsPdfOnly] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const documentSummary = getDocumentIntelligenceSummary({
    fileName,
    sheetName: selectedSheet,
    headers,
    rows,
  });

  const handleFile = useCallback(async (file) => {
    setError('');

    try {
      const result = await parseFile(file);
      setFileData(result);
      setFileName(result.fileName || file.name);
      setIsPdfOnly(Boolean(result.pdfOnly));

      if (result.pdfOnly) {
        setSelectedSheet(null);
        setHeaders([]);
        setRows([]);
        setMappings({});
        setStep('preview');
        return;
      }

      if (result.sheets) {
        if (Object.keys(result.sheets).length === 1) {
          const sheetName = Object.keys(result.sheets)[0];
          const { headers: nextHeaders, rows: nextRows } = result.sheets[sheetName];
          const autoMappings = {};
          nextHeaders.forEach((column) => {
            autoMappings[column] = detectUploadField(column);
          });

          setSelectedSheet(sheetName);
          setHeaders(nextHeaders);
          setRows(nextRows);
          setMappings(autoMappings);
          setStep('mapping');
          return;
        }

        setSelectedSheet(result.selectedSheet || Object.keys(result.sheets)[0]);
        setStep('sheet');
        return;
      }

      const autoMappings = {};
      result.headers.forEach((column) => {
        autoMappings[column] = detectUploadField(column);
      });

      setSelectedSheet(null);
      setHeaders(result.headers);
      setRows(result.rows);
      setMappings(autoMappings);
      setStep('mapping');
    } catch (uploadError) {
      setError(uploadError.message);
    }
  }, []);

  const handleSheetSelect = () => {
    if (!fileData?.sheets?.[selectedSheet]) {
      return;
    }

    const { headers: nextHeaders, rows: nextRows } = fileData.sheets[selectedSheet];
    const autoMappings = {};
    nextHeaders.forEach((column) => {
      autoMappings[column] = detectUploadField(column);
    });

    setHeaders(nextHeaders);
    setRows(nextRows);
    setMappings(autoMappings);
    setStep('mapping');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleConfirmMapping = () => {
    setStep('preview');
  };

  const buildTenants = () => {
    return rows
      .map((row) => {
        const tenant = {};
        let hasData = false;

        Object.entries(mappings).forEach(([column, field]) => {
          if (field === 'skip' || !field) {
            return;
          }

          const value = cleanValue(row[column], field);
          if (value !== '' && value !== 0) {
            hasData = true;
          }
          tenant[field] = value;
        });

        return hasData ? tenant : null;
      })
      .filter(Boolean)
      .filter((tenant) => tenant.unit || tenant.name);
  };

  const previewRows = rows.slice(0, 5).map((row) => {
    const mappedRow = {};
    Object.entries(mappings).forEach(([column, field]) => {
      if (field !== 'skip' && field) {
        mappedRow[field] = cleanValue(row[column], field);
      }
    });
    return mappedRow;
  });

  const handleConfirmUpload = async () => {
    if (isPdfOnly) {
      setError(`${documentSummary.label} PDFs can now be identified, but spreadsheet export or PMS sync is still required for tenant import.`);
      return;
    }

    if (!documentSummary.supportsTenantImport) {
      setError(`${documentSummary.label} files should be reviewed in analytics mode, not imported directly as tenants.`);
      return;
    }

    await onDataParsed(buildTenants(), {
      ...documentSummary,
      fileName,
      sheetName: selectedSheet || null,
      rowCount: rows.length,
    });
  };

  const onDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const autoDetectedCount = Object.entries(mappings).filter(([column, field]) => {
    return field !== 'skip' && detectUploadField(column) !== 'skip';
  }).length;

  const renderDocumentIntelligenceCard = (tone = 'default') => {
    const palette = tone === 'ready'
      ? 'bg-green-50 border-green-200'
      : documentSummary.supportsTenantImport && !isPdfOnly
        ? 'bg-blue-50 border-blue-200'
        : 'bg-amber-50 border-amber-200';

    return (
      <div className={`rounded-xl border p-4 ${palette}`}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Document Intelligence</p>
          <span className="rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-gray-600">
            {documentSummary.confidence} confidence
          </span>
          <span className="rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-gray-600">
            {documentSummary.dataCoverageLabel}
          </span>
          {documentSummary.sourceSystem !== 'unknown' && (
            <span className="rounded-full bg-white/80 px-2 py-1 text-xs font-medium text-gray-600">
              {documentSummary.sourceSystemLabel}
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-gray-900">{documentSummary.label}</p>
        <p className="mt-1 text-sm text-gray-600">{documentSummary.note}</p>
        <p className="mt-2 text-xs text-gray-500">{documentSummary.dataCoverageGuidance}</p>

        {documentSummary.signals.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {documentSummary.signals.map((signal) => (
              <span key={signal} className="rounded-full border border-white/80 bg-white/80 px-2.5 py-1 text-xs text-gray-600">
                {signal}
              </span>
            ))}
          </div>
        )}

        {documentSummary.unlocks?.length > 0 && (
          <div className="mt-4 grid gap-2 md:grid-cols-3">
            {documentSummary.unlocks.map((item) => (
              <div key={item} className="rounded-lg bg-white/80 p-3 text-xs text-gray-600">
                {item}
              </div>
            ))}
          </div>
        )}

        {documentSummary.nextBestUpload && (
          <p className="mt-3 text-xs font-medium text-gray-600">
            Next best file: {documentSummary.nextBestUpload}
          </p>
        )}
      </div>
    );
  };

  return (
    <div>
      {step === 'upload' && (
        <div
          onClick={() => fileRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={
            'border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ' +
            (dragOver ? 'border-tenafi-green bg-green-50' : 'border-gray-300 hover:border-tenafi-green hover:bg-gray-50')
          }
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.pdf"
            className="hidden"
            onChange={(event) => event.target.files[0] && handleFile(event.target.files[0])}
          />
          <p className="font-semibold text-gray-700 mb-1">Drop property data here</p>
          <p className="text-sm text-gray-500">
            Supports .CSV, .XLSX, .XLS, and PDF identification from AppFolio, Yardi, Entrata, SymmetryRE, and similar systems
          </p>
          <p className="mt-4 text-xs text-gray-400">Click to browse or drag and drop</p>
        </div>
      )}

      {step === 'sheet' && fileData?.sheets && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800">This workbook has multiple sheets. Which one should Tenafi use?</h3>
          <div className="space-y-2">
            {Object.keys(fileData.sheets).map((sheetName) => {
              const sheetSummary = getDocumentIntelligenceSummary({
                fileName,
                sheetName,
                headers: fileData.sheets[sheetName].headers,
                rows: fileData.sheets[sheetName].rows,
              });

              return (
                <label key={sheetName} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="sheet"
                    value={sheetName}
                    checked={selectedSheet === sheetName}
                    onChange={() => setSelectedSheet(sheetName)}
                    className="accent-tenafi-green"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">{sheetName}</span>
                      <span className="text-sm text-gray-400">({fileData.sheets[sheetName].rows.length} rows)</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{sheetSummary.label}</p>
                  </div>
                </label>
              );
            })}
          </div>
          <button
            onClick={handleSheetSelect}
            className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark"
          >
            Use This Sheet
          </button>
        </div>
      )}

      {step === 'mapping' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Map your columns to Tenafi fields</h3>
            <span className="text-xs text-gray-500 bg-green-50 border border-green-200 text-green-700 px-2 py-1 rounded-full">
              Auto-detected {autoDetectedCount}/{headers.length}
            </span>
          </div>

          {headers.length > 0 && renderDocumentIntelligenceCard()}

          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Your Column</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Sample Data</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Tenafi Field</th>
                  <th className="w-8 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {headers.map((column) => {
                  const isAutoDetected = detectUploadField(column) !== 'skip';
                  const sample = rows.slice(0, 3).map((row) => row[column]).filter(Boolean).join(', ');

                  return (
                    <tr key={column} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{column}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{sample || '-'}</td>
                      <td className="px-4 py-3">
                        <select
                          value={mappings[column] || 'skip'}
                          onChange={(event) => setMappings((current) => ({ ...current, [column]: event.target.value }))}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                        >
                          <option value="skip">Skip / Ignore</option>
                          {Object.entries(FIELD_LABELS)
                            .filter(([field]) => field !== 'skip')
                            .map(([field, label]) => (
                              <option key={field} value={field}>
                                {label}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center text-xs font-semibold">
                        {isAutoDetected ? 'OK' : '!'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('upload')}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleConfirmMapping}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark"
            >
              Preview Data ({rows.length} rows)
            </button>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">{isPdfOnly ? 'Upload Analysis' : 'Preview (first 5 rows)'}</h3>
            {!isPdfOnly && <span className="text-sm text-gray-500">{buildTenants().length} tenants will be imported</span>}
          </div>

          {renderDocumentIntelligenceCard('ready')}

          {previewRows.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {Object.keys(previewRows[0] || {}).map((field) => (
                      <th key={field} className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">
                        {FIELD_LABELS[field] || field}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {previewRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50">
                      {Object.values(row).map((value, valueIndex) => (
                        <td key={valueIndex} className="px-4 py-3 text-gray-700">
                          {formatCellValue(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {isPdfOnly && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              PDF upload is now identified and routed, but tenant import still requires spreadsheet export or a direct PMS integration so Tenafi can map resident-level fields safely.
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => setStep(isPdfOnly ? 'upload' : 'mapping')}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleConfirmUpload}
              disabled={isLoading || isPdfOnly}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark disabled:opacity-50"
            >
              {isPdfOnly ? 'Spreadsheet Export Required' : isLoading ? 'Importing...' : `Import ${buildTenants().length} Tenants`}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
